import type { RecordIssueRequest } from "@/lib/api";

/**
 * What the app says, and does, when a problem is not the resident's to fix.
 *
 * Pure and separate from the screens for the same reason `connector-issue.ts`
 * is: this is the product's promise about what happens next, and it has to
 * read the same wherever it is made. Two surfaces make it today, the connector
 * screens' `DiagnosisNotice` and the help chat's escalation card, and neither
 * is allowed to write its own version.
 */

/**
 * What an escalation always ends on, after whatever detail there is.
 *
 * Fixed text rather than anything the backend or the model writes: it is a
 * promise about what VELUX will do next, so it has to read the same on every
 * verdict that lands here, and it must not drift with a diagnosis wording
 * change. A resident is never told to contact anyone; we record the problem
 * and VELUX reaches out.
 */
export const ESCALATE_PROMISE =
  "This is not something you can fix from here. We have noted the problem and will contact VELUX support for you. They will reach out to get it sorted. You can carry on with the rest of the setup.";

/**
 * The same promise, for a step the rest of the setup waits on.
 *
 * `ESCALATE_PROMISE` ends on "carry on with the rest of the setup", which is
 * true of a compartment and false of the hub: no module after it can be
 * provisioned, mapped or read, so there is nothing to carry on with and no
 * button offering it. The last sentence therefore says what the resident can
 * do instead, which is put the phone down without losing anything.
 *
 * Two constants rather than one assembled from parts: both are promises about
 * what VELUX will do next, and a promise is worth reading whole in the file
 * that makes it.
 */
export const ESCALATE_PROMISE_PAUSED =
  "This is not something you can fix from here. We have noted the problem and will contact VELUX support for you. They will reach out to get it sorted. Your progress is saved, so you can close this page until then.";

/**
 * The steps a household can be carried past.
 *
 * Exactly the hardware compartments `flow-shell.tsx` hands an `onSkip`: each
 * one can fail on something nobody in the room can fix, and the rest of the
 * install sits behind it. The hub and network steps are deliberately absent —
 * they are prerequisites, so skipping one only moves the dead end a screen
 * later — and Air Quality carries its own deferrals inside Part B. Keep this
 * in step with the compartment block in `flow-shell.tsx`.
 */
const SKIPPABLE_STEPS = new Set(["windows", "floor_heating", "ventilation"]);

/** Whether this step has a skip to offer. Anything off the flow does not. */
export function canSkipStep(stepKey: string | null | undefined): boolean {
  return stepKey != null && SKIPPABLE_STEPS.has(stepKey);
}

/**
 * What the escalation card says when its way on did not take.
 *
 * Short on purpose. The flow shell already writes a fuller error of its own,
 * but it writes it onto the screen behind the sheet, where somebody reading
 * the card cannot see it. This is the same news, said where the tap happened.
 */
export const SKIP_FAILED_MESSAGE = "That did not go through. Try again in a moment.";

/**
 * What the help chat does once the flow shell's skip has answered.
 *
 * Pure, so the rule is checked without a component-testing stack: the sheet
 * closes only on a skip that actually moved the household on. A skip the shell
 * declined (one already in flight) and a skip that failed both come back
 * false, and both have to leave the sheet open. Closing on either would drop
 * the resident back onto the dead end with no sign the tap did nothing, and
 * onto a compartment that is still theirs to finish.
 */
export function escalationSkipOutcome(skipped: boolean): { close: boolean; error: string } {
  return skipped ? { close: true, error: "" } : { close: false, error: SKIP_FAILED_MESSAGE };
}

/**
 * Anything shaped like an email address. Loose on purpose: this decides
 * whether to withhold text, so over-matching costs a generic paragraph and
 * under-matching costs the promise.
 */
const ADDRESS = /[^\s@]+@[^\s@]+\.[a-z]{2,}/i;

/**
 * What the transcript shows for one assistant answer, or null to show nothing.
 *
 * Two jobs, both about the same rule: a resident is never handed an address to
 * write to. The backend strips them, and this is the second line of defence —
 * its two canned fallbacks (the model being unreachable, and a conversation
 * that has run its length) both still ship one, and both escalate.
 *
 * Null when the escalation card is already going to say it: the card carries
 * `ESCALATE_PROMISE` under its own heading, and a bubble repeating the same
 * paragraph directly above reads as a bug rather than as emphasis.
 */
export function answerForTranscript(answer: string, escalating: boolean): string | null {
  if (!ADDRESS.test(answer)) {
    return answer;
  }
  return escalating ? null : ESCALATE_PROMISE;
}

/**
 * What the report says was tried before the help chat gave up.
 *
 * Written for whoever reads the issue report, not for the resident: it has to
 * distinguish a problem the chat could not solve from a connector that never
 * came online, since both arrive as a skipped compartment.
 */
export const ASSISTANT_ESCALATION_TRIED =
  "Asked the help chat. The assistant could not find a fix the resident can carry out.";

/**
 * The step key to file an escalation under when the chat was opened off the
 * flow (the guide's `?`). Mirrors the backend's `HOME_SCREEN_KEY` sentinel, so
 * the report names the same place the assistant's own context does.
 */
const OFF_FLOW_STEP_KEY = "guide";

/**
 * One unsolved problem, as the issue report wants it.
 *
 * `detail` is the assistant's own words rather than what the resident saw:
 * this row is read by support, and the model's answer (including a canned
 * "I can't reach the assistant right now") is the evidence of what happened.
 * The upsert key is household + step + verdict, so a household that escalates
 * the same step twice updates one row instead of filing two.
 */
export function assistantEscalationRecord(args: {
  stepKey: string | null | undefined;
  answer: string;
  conversationId: string;
}): RecordIssueRequest {
  return {
    step_key: args.stepKey ?? OFF_FLOW_STEP_KEY,
    stage: "assistant",
    verdict: "assistant_escalated",
    // The resident read ESCALATE_PROMISE in the chat, so the report must say
    // VELUX will reach out. The backend keys that line off this action.
    action: "escalate",
    detail: args.answer,
    what_was_tried: ASSISTANT_ESCALATION_TRIED,
    conversation_id: args.conversationId,
  };
}

/** What the report reads on a correction row. Fixed words, like the escalation's. */
export const ASSISTANT_NOTE_TRIED =
  "The resident corrected the house record in the help chat and carried on with the step.";

/**
 * A correction the resident can carry on from, as the issue report wants it.
 *
 * Beta Test House 17: two Entrance windows the record lists as electric are
 * opened by hand. The chat tells the resident to finish the windows step with
 * the three that exist, and this row tells VELUX what to fix. No `action`, so
 * the report does not promise a callback for something nobody needs to visit.
 */
export function assistantNoteRecord(args: {
  stepKey: string | null | undefined;
  note: string;
  conversationId: string;
}): RecordIssueRequest {
  return {
    step_key: args.stepKey ?? OFF_FLOW_STEP_KEY,
    stage: "assistant",
    verdict: "record_correction",
    detail: args.note,
    what_was_tried: ASSISTANT_NOTE_TRIED,
    conversation_id: args.conversationId,
  };
}
